import * as z from 'zod';

const idLike = z.union([z.string(), z.number()]);
const numLike = z.coerce.number();

/** One payout level of a bonus slot: "N business orders pay M". */
export const slotTierSchema = z
  .object({
    id: idLike.nullish(),
    amount: numLike.nullish(),
    business_order_count: numLike.nullish(),
  })
  .loose();
export type SlotTier = z.infer<typeof slotTierSchema>;

/**
 * A bonus slot — the backend's "bonus period" (`admin/bonus-periods`). Loose:
 * the table also reads legacy columns (`reserve_limit`, `working_hours`, …)
 * that only some records carry, and a new field must not break the page.
 * `null` on the three limits means "no limit".
 */
export const slotSchema = z
  .object({
    id: idLike,
    /** Accrual period: `daily` (whole day) or `hourly`. */
    type: z.string().nullish(),
    bonuses: z.array(slotTierSchema).nullish(),
    active_from: z.string().nullish(),
    active_till: z.string().nullish(),
    from_time: z.string().nullish(),
    till_time: z.string().nullish(),
    created_at: z.string().nullish(),
    reserve_count: numLike.nullish(),
    reserve_limit: numLike.nullish(),
    min_order_count: numLike.nullish(),
    working_hours: numLike.nullish(),
    offer_reject_count: numLike.nullish(),
    cancelled_count: numLike.nullish(),
    cancel_order_count: numLike.nullish(),
    return_count: numLike.nullish(),
    returned_order_count: numLike.nullish(),
    /** Equipment the slot applies to; empty means all equipment. */
    equipment_ids: z.array(numLike).nullish(),
  })
  .loose();
export type Slot = z.infer<typeof slotSchema>;

export const slotListSchema = z.object({
  data: z.array(slotSchema).default([]),
  count: numLike.nullish(),
});
export type SlotList = z.infer<typeof slotListSchema>;

/** A courier's reservation of a slot (`admin/bonuses/reserves`). */
export const slotReserveSchema = z
  .object({
    courier_id: idLike.nullish(),
    date: z.string().nullish(),
    created_at: z.string().nullish(),
  })
  .loose();
export type SlotReserve = z.infer<typeof slotReserveSchema>;

export const slotReserveListSchema = z.object({
  data: z.array(slotReserveSchema).default([]),
  count: numLike.nullish(),
});
export type SlotReserveList = z.infer<typeof slotReserveListSchema>;

/** Equipment option for the "applies to" multiselect (`admin/equipments`). */
export const slotEquipmentSchema = z
  .object({
    id: numLike,
    name_ru: z.string().nullish(),
  })
  .loose();
export type SlotEquipment = z.infer<typeof slotEquipmentSchema>;
